import React, { useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { t } from 'i18next';
import moment from 'moment';

import CategoryDataBarChart from '../../common/CategoryDataBarChart/CategoryDataBarChart';
import DataStateHandler from '../../common/DataStateHandler/DataStateHandler';
import { ME } from '../../../graphql/auth';
import {
  getContestsQuery,
  getLeaderboardsQuery,
  OPT_OUT_OF_CONTESTS,
} from '../../../graphql/contest/contests';
import { Colors } from '../../../theme/shared';
import { AuthorizationContext } from '../../../contexts/AuthorizationContext';
import NumberUtils from '../../../utils/numberUtils';
import LeaderboardCategoryPoints from './LeaderboardCategoryPoints';
import AppInfo from '../../common/AppInfo';
import { MemberToastContext } from '../../../contexts/MemberToastContext';

const useStyles = makeStyles((theme) => ({
  cardsContainer: {
    justifyContent: 'space-evenly',
    marginTop: 8,
    padding: 16,
    borderRadius: 16,
    backgroundColor: theme.palette.primary.light,
  },
  cardContainer: {
    padding: '0px 8px',
  },
  card: {
    alignItems: 'center',
    flexDirection: 'column',
    padding: 8,
    borderRadius: 16,
    backgroundColor: theme.palette.backgrounds.white,
  },
  title: {
    color: theme.palette.text.primary,
    margin: '24px 0px',
  },
  subtitleContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  subtitle: {
    color: Colors.TertiaryText,
    textTransform: 'uppercase',
  },
}));

type Props = {
  contestId: string;
  rollup: string;
};

const LeaderboardDetails: React.FC<Props> = ({ contestId, rollup }) => {
  const classes = useStyles();
  const { user } = useContext(AuthorizationContext);
  const { showToast } = useContext(MemberToastContext);
  const [optOutOfContests] = useMutation(OPT_OUT_OF_CONTESTS, {
    refetchQueries: [ME],
  });
  const optedOut = user?.optOutOfContests || false;

  const {
    loading: loadingContests,
    error: errorContests,
    data: { findCompanyContests: contests = [] } = {},
  } = useQuery(getContestsQuery(true), {
    variables: {
      contestIds: [contestId],
    },
  });

  const {
    loading: loadingLeaderboards,
    error: errorLeaderboards,
    data: { leaderboards = [] } = {},
  } = useQuery(getLeaderboardsQuery(true, false), {
    variables: {
      contestId,
      rollup,
    },
    skip: optedOut,
  });

  const userRank = leaderboards[0]?.contestRanking?.find(
    (cr) => cr.userId === user?.id,
  )?.rank;
  const userPoints = contests[0]?.userPoints;
  const totalPoints = userPoints?.reduce(
    (acc, cur) => acc + cur.totalPoints,
    0,
  );

  const chartData = {
    id: '1',
    label: 'All Categories',
    data: [
      {
        id: '1',
        label: 'All Categories',
        color: Colors.CobaltBlue,
        data:
          userPoints?.map((up) => ({
            date: moment(up.month.toString()).format(),
            value: up.totalPoints,
          })) || [],
      },
    ],
  };

  const handleOptIn = async () => {
    showToast({
      messages: [
        {
          title: t('contest.optIn.toastTitle'),
          description: t('contest.optIn.toastDescription'),
        },
      ],
    });
    await optOutOfContests({ variables: { optOut: false } });
  };

  return (
    <DataStateHandler
      loading={loadingContests || loadingLeaderboards}
      error={errorContests || errorLeaderboards}>
      <Grid container>
        <Typography variant="h3">
          {t('contest.leaderboardDetailsPage')}
        </Typography>
      </Grid>
      <Grid container justifyContent="center">
        <Grid container item className={classes.cardsContainer}>
          {!optedOut && userRank && (
            <Grid container item className={classes.cardContainer} xs={6}>
              <Grid container item className={classes.card}>
                <Typography variant="h2" color={Colors.CobaltBlue}>
                  {NumberUtils.formatOrdinal(userRank)}
                </Typography>
                <Typography variant="body2" color={Colors.TertiaryText}>
                  {t('place')}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid
            container
            item
            className={classes.cardContainer}
            xs={optedOut || !userRank ? 12 : 6}>
            <Grid container item className={classes.card}>
              <Typography variant="h2" color={Colors.CobaltBlue}>
                {totalPoints}
              </Typography>
              <Typography variant="body2" color={Colors.TertiaryText}>
                {t('contest.pointsEarnedSoFar')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid container item>
          <Typography className={classes.title} variant="h3">
            {t('contest.pointsBreakdown')}
          </Typography>
          <Grid container item spacing={2}>
            <Grid container item direction="column" xs={6}>
              <Grid container item className={classes.subtitleContainer}>
                <Typography className={classes.subtitle} variant="subtitle1">
                  {t('contest.pointsEarnedByMonth')}
                </Typography>
                <AppInfo id="leaderboard_points_by_month" />
              </Grid>
              <CategoryDataBarChart
                categoryData={chartData}
                dateFormat="MMMM"
              />
            </Grid>
            <Grid container item direction="column" xs={6}>
              <Grid container item className={classes.subtitleContainer}>
                <Typography className={classes.subtitle} variant="subtitle1">
                  {t('contest.pointsEarnedByCategory')}
                </Typography>
                <AppInfo id="leaderboard_points_by_category" />
              </Grid>
              <LeaderboardCategoryPoints userPoints={userPoints} />
            </Grid>
          </Grid>
        </Grid>
        {optedOut && (
          <Button onClick={handleOptIn}>
            <Typography variant="subtitle2" color={Colors.TertiaryText}>
              {t('contest.optIn.button')}
            </Typography>
          </Button>
        )}
      </Grid>
    </DataStateHandler>
  );
};

export default LeaderboardDetails;
